import React, { FC, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import { useAppTheme } from "@/utils/theme";
import { useUserStore } from "@/hooks/useUserStore";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import {
  useCreateEducationalResource,
  useUpdateEducationalResource,
} from "@/hooks/useEducationalResources";
import { useEducationalResourceAudit } from "@/hooks/useEducationalResourceAudit";
import {
  DEFAULT_RESOURCE_LANGUAGE,
  EducationalResource,
  EducationalResourceType,
  RESOURCE_LANGUAGES,
  RESOURCE_LANGUAGE_LABELS,
  RESOURCE_STATUSES,
  RESOURCE_STATUS_LABELS,
  RESOURCE_TYPES,
  RESOURCE_TYPE_LABELS,
  ResourceCategoryItem,
  categoryItemName,
  hasDuration,
} from "@/domain/educationalResource.interface";
import { ErrorResponse } from "@/domain/error.interface";
import { educationalResourceSchema } from "./validation.schema";
import {
  EducationalResourceFormValues,
  emptyLanguage,
  toEducationalResourceFormValues,
  toEducationalResourcePayload,
} from "./formValues";

const MAX_LANGUAGES = RESOURCE_LANGUAGES.length;
const TID = "educational-resources";

type Props = {
  open: boolean;
  onClose: () => void;
  /** Present in edit mode. */
  resource?: EducationalResource;
  /** All items of the account's EDUCATIONAL_RESOURCE category (active and inactive). */
  categories: ResourceCategoryItem[];
  isLoadingCategories: boolean;
};

const errorMessage = (error: unknown): string => {
  const err = (error || {}) as Partial<ErrorResponse>;
  const message = Array.isArray(err.message) ? err.message[0] : err.message;
  return message || "An error occurred";
};

const EducationalResourceModal: FC<Props> = ({
  open,
  onClose,
  resource,
  categories,
  isLoadingCategories,
}) => {
  const theme = useAppTheme();
  const isEdit = Boolean(resource);
  const { userInfo } = useUserStore();
  const { showSnackBar } = useFeedBack();
  const audit = useEducationalResourceAudit();
  const createResource = useCreateEducationalResource();
  const updateResource = useUpdateEducationalResource();
  const isPending = createResource.isPending || updateResource.isPending;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<EducationalResourceFormValues>({
    resolver: yupResolver(educationalResourceSchema) as never,
    defaultValues: toEducationalResourceFormValues(resource),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "languages",
  });

  useEffect(() => {
    if (open) reset(toEducationalResourceFormValues(resource));
  }, [open, resource, reset]);

  const selectedType = watch("type");
  const watchedLanguages = watch("languages");
  const usedLanguages = (watchedLanguages || []).map((l) => l?.language);

  // ACTIVE items only; in edit mode the resource's current item is kept even if now inactive,
  // so an unchanged category can be saved (the API re-checks the category only when it changes).
  const categoryOptions = useMemo(
    () =>
      (categories || [])
        .filter(
          (item) =>
            item.status !== "INACTIVE" || item._id === resource?.categoryId
        )
        .map((item) => ({ key: item._id, value: categoryItemName(item) })),
    [categories, resource?.categoryId]
  );
  const noCategories = !isLoadingCategories && categoryOptions.length === 0;

  const onSubmit = async (values: EducationalResourceFormValues) => {
    const userId = userInfo?._id ?? "";
    try {
      if (resource) {
        await updateResource.mutateAsync({
          id: resource._id,
          data: toEducationalResourcePayload(values, "edit", userId),
        });
        audit("EDIT", resource._id);
        showSnackBar("The educational resource has been updated.", "success");
      } else {
        const created = await createResource.mutateAsync(
          toEducationalResourcePayload(values, "create", userId)
        );
        audit("ADD", created?._id);
        showSnackBar("The educational resource has been created.", "success");
      }
      onClose();
    } catch (error: unknown) {
      showSnackBar(errorMessage(error), "error");
    }
  };

  const addLanguage = () => {
    const next = RESOURCE_LANGUAGES.find((c) => !usedLanguages.includes(c));
    if (next) append(emptyLanguage(next));
  };

  const languagesError =
    (errors.languages as { root?: { message?: string } } | undefined)?.root
      ?.message ||
    (errors.languages as { message?: string } | undefined)?.message;

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      theme={theme}
      title={isEdit ? "Edit educational resource" : "New educational resource"}
      dataTestId={`${TID}-modal-dialog`}
      closeButtonTestId={`${TID}-modal-close-button`}
      size={{ width: "100%", minHeight: 560, maxHeight: 860, maxWidth: 880 }}
      content={
        <Box sx={{ width: "100%", pt: 1 }}>
          <Grid container spacing="20px">
            <Grid item xs={12} sm={6}>
              <Controller
                name="categoryId"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Category"
                    fullWidth
                    required
                    data-testid={`${TID}-form-category-select`}
                    disabled={isPending || isLoadingCategories}
                    error={!!errors.categoryId}
                    helperText={
                      errors.categoryId?.message ||
                      (noCategories
                        ? "No categories yet. Create them in Categories."
                        : "")
                    }
                  >
                    {categoryOptions.length === 0 && (
                      <MenuItem value="" disabled>
                        No categories yet. Create them in Categories.
                      </MenuItem>
                    )}
                    {categoryOptions.map((o) => (
                      <MenuItem
                        key={o.key}
                        value={o.key}
                        data-testid={`${TID}-form-category-option-${o.key}`}
                      >
                        {o.value}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Type"
                    fullWidth
                    required
                    data-testid={`${TID}-form-type-select`}
                    disabled={isPending}
                    error={!!errors.type}
                    helperText={errors.type?.message || ""}
                    onChange={(e) => {
                      const next = e.target.value as EducationalResourceType;
                      field.onChange(next);
                      if (!hasDuration(next)) {
                        setValue("duration", "");
                        clearErrors("duration");
                      }
                    }}
                  >
                    {RESOURCE_TYPES.map((t) => (
                      <MenuItem
                        key={t}
                        value={t}
                        data-testid={`${TID}-form-type-option-${t}`}
                      >
                        {RESOURCE_TYPE_LABELS[t]}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Status"
                    fullWidth
                    required
                    data-testid={`${TID}-form-status-select`}
                    disabled={isPending}
                    error={!!errors.status}
                    helperText={errors.status?.message || ""}
                  >
                    {RESOURCE_STATUSES.map((s) => (
                      <MenuItem
                        key={s}
                        value={s}
                        data-testid={`${TID}-form-status-option-${s}`}
                      >
                        {RESOURCE_STATUS_LABELS[s]}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={hasDuration(selectedType) ? 8 : 12}>
              <Controller
                name="thumbnailUrl"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || ""}
                    label="Thumbnail URL"
                    placeholder="https://"
                    fullWidth
                    disabled={isPending}
                    inputProps={{
                      "data-testid": `${TID}-form-thumbnail-input`,
                    }}
                    error={!!errors.thumbnailUrl}
                    helperText={errors.thumbnailUrl?.message || ""}
                  />
                )}
              />
            </Grid>
            {hasDuration(selectedType) && (
              <Grid item xs={12} sm={4}>
                <Controller
                  name="duration"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ""}
                      type="number"
                      label="Duration (seconds)"
                      fullWidth
                      disabled={isPending}
                      inputProps={{
                        min: 0,
                        step: 1,
                        "data-testid": `${TID}-form-duration-input`,
                      }}
                      error={!!errors.duration}
                      helperText={errors.duration?.message || ""}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>

          <Stack spacing={1.5} sx={{ mt: 3 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="labelLarge" component="div">
                Languages *
              </Typography>
              {fields.length < MAX_LANGUAGES && (
                <Button
                  variantType="save"
                  label="Add language"
                  onClick={addLanguage}
                  disabled={isPending}
                  dataTestId={`${TID}-add-language-button`}
                />
              )}
            </Stack>

            {languagesError && (
              <Typography variant="bodySmall" color="error">
                {languagesError}
              </Typography>
            )}

            {fields.map((f, idx) => {
              const isLocked =
                idx === 0 && f.language === DEFAULT_RESOURCE_LANGUAGE;
              return (
                <Box
                  key={f.id}
                  data-testid={`${TID}-language-card-${idx}`}
                  sx={{
                    p: 1.5,
                    border: "1px dashed",
                    borderColor: theme.palette.outline.main,
                    borderRadius: "8px",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="titleSmall" component="div">
                      Language #{idx + 1}
                    </Typography>
                    <IconButton
                      aria-label="Remove language"
                      data-testid={`${TID}-language-${idx}-remove-button`}
                      onClick={() => remove(idx)}
                      disabled={isPending || isLocked}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Controller
                        name={`languages.${idx}.language`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            select
                            label="Language"
                            fullWidth
                            required
                            data-testid={`${TID}-language-${idx}-select`}
                            disabled={isPending || isLocked}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                          >
                            {RESOURCE_LANGUAGES.map((code) => (
                              <MenuItem
                                key={code}
                                value={code}
                                data-testid={`${TID}-language-${idx}-option-${code}`}
                                disabled={
                                  code !== field.value &&
                                  usedLanguages.includes(code)
                                }
                              >
                                {RESOURCE_LANGUAGE_LABELS[code]}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Controller
                        name={`languages.${idx}.title`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value || ""}
                            label="Title"
                            fullWidth
                            required
                            disabled={isPending}
                            inputProps={{
                              "data-testid": `${TID}-language-${idx}-title-input`,
                            }}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name={`languages.${idx}.description`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value || ""}
                            label="Description"
                            fullWidth
                            multiline
                            minRows={2}
                            disabled={isPending}
                            inputProps={{
                              "data-testid": `${TID}-language-${idx}-description-input`,
                            }}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name={`languages.${idx}.url`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value || ""}
                            label="URL"
                            placeholder="https://"
                            fullWidth
                            required
                            disabled={isPending}
                            inputProps={{
                              "data-testid": `${TID}-language-${idx}-url-input`,
                            }}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
          </Stack>
        </Box>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
            dataTestId={`${TID}-modal-cancel-button`}
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
            dataTestId={`${TID}-modal-save-button`}
          />
        </Box>
      }
    />
  );
};

export default EducationalResourceModal;
